import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sanitizeEmailHtml } from "@/lib/sanitize-html";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2,
  Inbox as InboxIcon,
  Mail,
  MailOpen,
  Reply,
  ReplyAll,
  Forward as ForwardIcon,
  RefreshCw,
  Send,
  Search as SearchIcon,
  Sparkles,
  Flame,
  Settings2,
  Check,
  RotateCw,
  X as XIcon,
  Paperclip,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { RichTextEditor } from "@/components/rich-text-editor";
import { CcEmailInput } from "@/components/cc-email-input";
import { EmailVariablesSelector } from "@/components/email-variables-selector";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { InboundEmail, InboxThreadSummary, EmailTemplate } from "@shared/schema";

// Task #601 — AI Reply Management category labels.
type InboxCategory =
  | "logistics_question"
  | "negotiation"
  | "objection"
  | "scheduling"
  | "confirmation"
  | "spam"
  | "other";

const CATEGORY_LABELS: Record<InboxCategory, string> = {
  logistics_question: "Logistics",
  negotiation: "Negotiation",
  objection: "Objection",
  scheduling: "Scheduling",
  confirmation: "Confirmation",
  spam: "Spam",
  other: "Other",
};

// Spam + confirmation + other = no draft, dimmer chip color.
const CATEGORY_TONE: Record<InboxCategory, string> = {
  scheduling: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  objection: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100",
  negotiation: "bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-100",
  logistics_question: "bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100",
  confirmation: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100",
  spam: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  other: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
};

// "Non-actionable" categories never get a draft generated automatically.
const CATEGORIES_WITHOUT_DRAFT = new Set<InboxCategory>(["spam", "confirmation", "other"]);

type InboxDraft = {
  id: string;
  inboundEmailId: string;
  channel: string;
  tone: string;
  body: string;
  status: "pending" | "approved" | "dismissed" | "edited";
  costCents: number | null;
  modelKey: string | null;
  createdAt: string;
  updatedAt: string;
};

type ChannelPrefs = Record<
  "email" | "sms" | "ig_dm" | "fb_dm",
  { enabled: boolean; aiDraftEnabled: boolean }
>;

type AttachmentDraft = { filename: string; contentBase64: string; contentType: string; size: number };

type ThreadItem = {
  type: "inbound" | "sent";
  id: string;
  from: string | null;
  to: string[] | null;
  cc: string[] | null;
  subject: string | null;
  bodyHtml: string | null;
  bodyPlainText: string | null;
  sentAt: string | null;
  sentByName?: string | null;
};

type InboxDetail = { message: InboundEmail; thread: ThreadItem[]; draft: InboxDraft | null };

type PriorityRow = {
  id: string;
  subject: string | null;
  fromEmail: string | null;
  bodyPlainText: string | null;
  receivedAt: string | null;
  aiCategory: InboxCategory | null;
  aiCategoryConfidence: number | null;
  isRead: boolean;
  threadId: string | null;
  draft: { id: string; status: string; bodyPreview: string; body: string } | null;
};

// Inbound HTML sanitizer. The shared helper at `@/lib/sanitize-html` is the
// single source of truth so the email hub + inbox + any future render path
// all run the same allow-list and the strict CSP can't be undermined by
// drift between sanitizers.
const sanitize = sanitizeEmailHtml;

function formatWhen(input: string | Date | null | undefined): string {
  if (!input) return "";
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function fromDisplay(addr: string | null | undefined): string {
  if (!addr) return "";
  const m = addr.match(/^([^<]+)<([^>]+)>$/);
  return m ? m[1].trim().replace(/^"|"$/g, "") : addr;
}

export function InboxPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Task #632 — when a non-email thread is selected, we render a different
  // detail pane (no Gmail thread / forward / send-via-Gmail buttons).
  const [selectedNonEmail, setSelectedNonEmail] = useState<{
    channel: "sms" | "ig_dm" | "fb_dm";
    sourceId: string;
    threadKey: string;
    fromHandle: string;
    body: string;
  } | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState<"reply" | "forward">("reply");
  const [composeBody, setComposeBody] = useState("");
  const [replyAll, setReplyAll] = useState(false);
  const [composeCc, setComposeCc] = useState("");
  const [forwardTo, setForwardTo] = useState("");
  const [attachments, setAttachments] = useState<AttachmentDraft[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  // Task #601 — list-pane view mode toggle. "priority" hides spam/other and
  // sorts by triage rank, "all" is the legacy thread list.
  const [viewMode, setViewMode] = useState<"all" | "priority">("all");
  // Tone passed to /draft on re-generate. Persists for the session so a rep
  // who likes "concise" doesn't have to re-pick it on every message.
  // Task #634 — initial value comes from the workspace voice defaults
  // (loaded below) so reps inherit the team's tone/length without picking.
  const [draftTone, setDraftTone] = useState<"professional" | "friendly" | "concise">("professional");
  const [draftLength, setDraftLength] = useState<"short" | "standard" | "detailed">("standard");
  const [draftDefaultsLoaded, setDraftDefaultsLoaded] = useState(false);
  // Threads the user manually marked unread in this session — we suppress the
  // auto-mark-read on re-select so they don't immediately flip back to read,
  // mirroring Gmail's behavior.
  const [manualUnreadThreadKeys, setManualUnreadThreadKeys] = useState<Set<string>>(new Set());

  // Pull the user's email templates so they can drop in canned responses while
  // composing a reply (parity with the standalone compose / mass-email flows).
  const { data: emailTemplates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates"],
  });

  // Debounce search input so we don't hammer the backend on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const threadsKey = ["/api/inbox/threads", { unreadOnly, search: debouncedSearch }] as const;
  const { data: threads = [], isLoading: listLoading, refetch } = useQuery<InboxThreadSummary[]>({
    queryKey: threadsKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (unreadOnly) params.set("unreadOnly", "1");
      if (debouncedSearch) params.set("search", debouncedSearch);
      const qs = params.toString();
      const res = await fetch(`/api/inbox/threads${qs ? `?${qs}` : ""}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load inbox");
      return res.json();
    },
  });

  const { data: detail, isLoading: detailLoading } = useQuery<InboxDetail>({
    queryKey: ["/api/inbox", selectedId],
    queryFn: async () => {
      const res = await fetch(`/api/inbox/${selectedId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load message");
      return res.json();
    },
    enabled: !!selectedId,
  });

  // Priority queue feed — only loaded when the user flips into Priority view.
  const { data: priorityRows = [], isLoading: priorityLoading, refetch: refetchPriority } = useQuery<PriorityRow[]>({
    queryKey: ["/api/inbox/priority", { unreadOnly }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (unreadOnly) params.set("unreadOnly", "true");
      const qs = params.toString();
      const res = await fetch(`/api/inbox/priority${qs ? `?${qs}` : ""}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load priority queue");
      return res.json();
    },
    enabled: viewMode === "priority",
  });

  // Per-user channel prefs popover. Lazy-loaded the first time the popover opens.
  const [prefsOpen, setPrefsOpen] = useState(false);
  const { data: channelPrefs } = useQuery<ChannelPrefs>({
    queryKey: ["/api/inbox/channel-prefs"],
    enabled: prefsOpen,
  });

  const updateChannelPref = useMutation({
    mutationFn: async (vars: { channel: keyof ChannelPrefs; enabled?: boolean; aiDraftEnabled?: boolean }) => {
      const { channel, ...body } = vars;
      const res = await apiRequest("PATCH", `/api/inbox/channel-prefs/${channel}`, body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/channel-prefs"] });
    },
  });

  // Task #634 — pull workspace voice defaults so the tone/length selectors
  // pre-fill with the team's chosen voice on first render.
  const { data: draftDefaults } = useQuery<{
    tone: "professional" | "friendly" | "concise";
    length: "short" | "standard" | "detailed";
  }>({ queryKey: ["/api/inbox/draft-defaults"] });
  useEffect(() => {
    // Apply once so a mid-session admin change doesn't yank a rep's
    // override out from under them while they're picking a tone.
    if (draftDefaults && !draftDefaultsLoaded) {
      setDraftTone(draftDefaults.tone);
      setDraftLength(draftDefaults.length);
      setDraftDefaultsLoaded(true);
    }
  }, [draftDefaults, draftDefaultsLoaded]);

  const generateDraftMutation = useMutation({
    mutationFn: async (vars: { id: string; tone: typeof draftTone; length: typeof draftLength }) => {
      const res = await apiRequest("POST", `/api/inbox/${vars.id}/draft`, {
        tone: vars.tone,
        length: vars.length,
      });
      return res.json() as Promise<InboxDraft>;
    },
    onSuccess: (_d, vars) => {
      toast({ title: "Draft ready" });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox", vars.id] });
    },
    onError: (err: any) => {
      let msg = err?.message || "Failed to generate draft";
      try {
        const m = msg.match(/^\d+:\s*(.+)$/);
        if (m) {
          const parsed = JSON.parse(m[1]);
          if (parsed?.message) msg = parsed.message;
        }
      } catch {}
      toast({ title: "Draft failed", description: msg, variant: "destructive" });
    },
  });

  const dismissDraftMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/inbox/${id}/draft/dismiss`, {}),
    onSuccess: (_r, id) => {
      toast({ title: "Draft dismissed" });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox", id] });
    },
  });

  // Task #632 — non-email inbox feed (SMS / IG DM / FB DM).
  type NonEmailThread = {
    channel: "sms" | "ig_dm" | "fb_dm";
    threadKey: string;
    latestSourceId: string;
    fromHandle: string;
    preview: string;
    body: string;
    lastReceivedAt: string | null;
    unreadCount: number;
    latestDraft: {
      id: string;
      status: "pending" | "approved" | "dismissed";
      bodyPreview: string;
      body: string;
      aiCategory: InboxCategory | null;
    } | null;
  };
  // Inline composer state for the non-email detail pane. Pre-filled by
  // Approve / Edit / Re-draft buttons; sent via the per-channel /send route.
  const [nonEmailComposeBody, setNonEmailComposeBody] = useState("");
  const [nonEmailComposeOpen, setNonEmailComposeOpen] = useState(false);
  const { data: nonEmailThreads = [] } = useQuery<NonEmailThread[]>({
    queryKey: ["/api/inbox/non-email/threads"],
  });

  const approveNonEmailDraftMutation = useMutation({
    mutationFn: async (vars: { channel: string; sourceId: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/inbox/non-email/${vars.channel}/${vars.sourceId}/draft/approve`,
        {},
      );
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Draft approved",
        description: "Edit and send when ready — nothing was sent automatically.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/non-email/threads"] });
    },
  });

  const regenerateNonEmailDraftMutation = useMutation({
    mutationFn: async (vars: { channel: string; sourceId: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/inbox/non-email/${vars.channel}/${vars.sourceId}/draft/regenerate`,
        {},
      );
      return res.json();
    },
    onSuccess: (data: { bodyPreview?: string; body?: string } | null) => {
      toast({ title: "Draft regenerated" });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/non-email/threads"] });
      // Pull the freshly drafted body straight into the composer if it's open.
      const next = (data?.body ?? data?.bodyPreview) || "";
      if (next) setNonEmailComposeBody(next);
    },
    onError: (err: Error) => {
      toast({ title: "Re-draft failed", description: err.message, variant: "destructive" });
    },
  });

  const sendNonEmailMutation = useMutation({
    mutationFn: async (vars: { channel: string; sourceId: string; body: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/inbox/non-email/${vars.channel}/${vars.sourceId}/send`,
        { body: vars.body },
      );
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Reply sent" });
      setNonEmailComposeOpen(false);
      setNonEmailComposeBody("");
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/non-email/threads"] });
    },
    onError: (err: Error) => {
      toast({ title: "Send failed", description: err.message, variant: "destructive" });
    },
  });

  const dismissNonEmailDraftMutation = useMutation({
    mutationFn: async (vars: { channel: string; sourceId: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/inbox/non-email/${vars.channel}/${vars.sourceId}/draft/dismiss`,
        {},
      );
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Draft dismissed" });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/non-email/threads"] });
    },
  });

  const channelLabel = (ch: "sms" | "ig_dm" | "fb_dm") =>
    ch === "sms" ? "SMS" : ch === "ig_dm" ? "Instagram DM" : "Facebook DM";

  const categorizeMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/inbox/${id}/categorize`, {}),
    onSuccess: (_r, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/inbox", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/priority"] });
    },
  });

  const overrideCategoryMutation = useMutation({
    mutationFn: (vars: { id: string; category: InboxCategory }) =>
      apiRequest("POST", `/api/inbox/${vars.id}/category/override`, { category: vars.category }),
    onSuccess: (_r, vars) => {
      toast({ title: `Re-labeled as ${CATEGORY_LABELS[vars.category]}` });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox", vars.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/priority"] });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: ({ id, isRead, thread }: { id: string; isRead: boolean; thread?: boolean }) =>
      apiRequest("POST", `/api/inbox/${id}/read`, { isRead, thread }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/unread-count"] });
    },
  });

  const resetCompose = () => {
    setComposeOpen(false);
    setComposeMode("reply");
    setComposeBody("");
    setComposeCc("");
    setForwardTo("");
    setReplyAll(false);
    setAttachments([]);
    setSelectedTemplateId(null);
  };

  // Build a Gmail-style quoted block of the original message for forwarding.
  // Keeps a small header (From / Date / Subject / To) followed by the original body.
  const buildForwardBody = (msg: { fromEmail: string | null; subject: string | null; receivedAt: string | null; toEmails: string[] | null; bodyHtml: string | null; bodyPlainText: string | null; }) => {
    const sentAtStr = msg.receivedAt ? new Date(msg.receivedAt).toLocaleString() : "";
    const toStr = (msg.toEmails || []).join(", ");
    const safeBody = msg.bodyHtml
      ? sanitize(msg.bodyHtml)
      : `<pre style="white-space: pre-wrap; font-family: inherit;">${(msg.bodyPlainText || "").replace(/[<>&]/g, (c) => ({"<":"&lt;",">":"&gt;","&":"&amp;"}[c] || c))}</pre>`;
    return [
      "<p></p>",
      "<p></p>",
      "<div style=\"border-left: 2px solid #ccc; padding-left: 12px; margin-top: 8px;\">",
      "<p style=\"margin: 0 0 8px 0; color: #555;\">---------- Forwarded message ----------</p>",
      `<p style="margin: 0; color: #555;"><strong>From:</strong> ${msg.fromEmail || ""}</p>`,
      sentAtStr ? `<p style="margin: 0; color: #555;"><strong>Date:</strong> ${sentAtStr}</p>` : "",
      `<p style="margin: 0; color: #555;"><strong>Subject:</strong> ${msg.subject || ""}</p>`,
      toStr ? `<p style="margin: 0 0 8px 0; color: #555;"><strong>To:</strong> ${toStr}</p>` : "",
      safeBody,
      "</div>",
    ].filter(Boolean).join("");
  };

  // Apply an email template to the compose body. The reply subject is auto-built
  // server-side as "Re: <original subject>", so a template only fills in the body.
  const handleTemplateChange = (templateId: string) => {
    if (templateId === "none") {
      setSelectedTemplateId(null);
      return;
    }
    const template = emailTemplates.find((t) => t.id === templateId);
    if (template) {
      setComposeBody(template.body || "");
      setSelectedTemplateId(templateId);
    }
  };

  const replyMutation = useMutation({
    mutationFn: ({ id, body, replyAll: ra, cc, attachments: atts }: {
      id: string;
      body: string;
      replyAll: boolean;
      cc?: string[];
      attachments?: AttachmentDraft[];
    }) =>
      apiRequest("POST", `/api/inbox/${id}/reply`, {
        body,
        replyAll: ra,
        cc: cc && cc.length > 0 ? cc : undefined,
        attachments: atts && atts.length > 0
          ? atts.map(a => ({ filename: a.filename, contentBase64: a.contentBase64, contentType: a.contentType }))
          : undefined,
      }),
    onSuccess: async () => {
      toast({ title: "Reply sent" });
      resetCompose();
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/unread-count"] });
      if (selectedId) {
        queryClient.invalidateQueries({ queryKey: ["/api/inbox", selectedId] });
      }
    },
    onError: async (err: any) => {
      let msg = err?.message || "Failed to send reply";
      try {
        const m = msg.match(/^\d+:\s*(.+)$/);
        if (m) {
          const parsed = JSON.parse(m[1]);
          if (parsed?.message) msg = parsed.message;
        }
      } catch {}
      toast({ title: "Reply failed", description: msg, variant: "destructive" });
    },
  });

  const forwardMutation = useMutation({
    mutationFn: ({ id, to, body, cc, attachments: atts }: {
      id: string;
      to: string;
      body: string;
      cc?: string[];
      attachments?: AttachmentDraft[];
    }) =>
      apiRequest("POST", `/api/inbox/${id}/forward`, {
        to,
        body,
        cc: cc && cc.length > 0 ? cc : undefined,
        attachments: atts && atts.length > 0
          ? atts.map(a => ({ filename: a.filename, contentBase64: a.contentBase64, contentType: a.contentType }))
          : undefined,
      }),
    onSuccess: async () => {
      toast({ title: "Message forwarded" });
      resetCompose();
      queryClient.invalidateQueries({ queryKey: ["/api/inbox/threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/inbox"] });
      if (selectedId) {
        queryClient.invalidateQueries({ queryKey: ["/api/inbox", selectedId] });
      }
    },
    onError: async (err: any) => {
      let msg = err?.message || "Failed to forward message";
      try {
        const m = msg.match(/^\d+:\s*(.+)$/);
        if (m) {
          const parsed = JSON.parse(m[1]);
          if (parsed?.message) msg = parsed.message;
        }
      } catch {}
      toast({ title: "Forward failed", description: msg, variant: "destructive" });
    },
  });

  const handleSelect = (thread: InboxThreadSummary) => {
    setSelectedId(thread.latestMessageId);
    // Clear non-email selection so the detail pane swaps back to email.
    setSelectedNonEmail(null);
    setNonEmailComposeOpen(false);
    setNonEmailComposeBody("");
    setComposeOpen(false);
    setComposeMode("reply");
    setComposeBody("");
    setForwardTo("");
    // Skip auto-mark-read if the user just manually marked this thread unread.
    const threadKey = thread.threadId || thread.latestMessageId;
    if (thread.unreadCount > 0 && !manualUnreadThreadKeys.has(threadKey)) {
      // Mark the entire thread read in one call when it has a Gmail thread id.
      markReadMutation.mutate({
        id: thread.latestMessageId,
        isRead: true,
        thread: !!thread.threadId,
      });
    }
  };

  const handleMarkUnread = () => {
    if (!selectedId || !detail) return;
    const threadId = detail.message.threadId;
    const threadKey = threadId || selectedId;
    setManualUnreadThreadKeys((prev) => {
      const next = new Set(prev);
      next.add(threadKey);
      return next;
    });
    markReadMutation.mutate(
      { id: selectedId, isRead: false, thread: !!threadId },
      {
        onSuccess: () => {
          toast({ title: "Marked unread" });
        },
      }
    );
  };

  const handleStartForward = () => {
    if (!detail) return;
    setComposeMode("forward");
    setReplyAll(false);
    setComposeCc("");
    setForwardTo("");
    setAttachments([]);
    setSelectedTemplateId(null);
    setComposeBody(buildForwardBody(detail.message));
    setComposeOpen(true);
  };

  const handleSendForward = () => {
    if (!selectedId) return;
    const to = forwardTo.trim();
    if (!to) {
      toast({ title: "Recipient required", description: "Enter a To address before forwarding.", variant: "destructive" });
      return;
    }
    // Quick email shape check — server will re-validate with Zod email().
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast({ title: "Invalid email", description: `"${to}" is not a valid email address.`, variant: "destructive" });
      return;
    }
    const tmp = document.createElement("div");
    tmp.innerHTML = composeBody;
    const text = (tmp.textContent || "").trim();
    if (!text) return;
    const cc = composeCc
      ? composeCc.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    forwardMutation.mutate({
      id: selectedId,
      to,
      body: composeBody,
      cc,
      attachments,
    });
  };

  const handleSendReply = () => {
    if (!selectedId) return;
    // RichTextEditor returns "<p></p>" when empty — guard on the trimmed text content.
    const tmp = document.createElement("div");
    tmp.innerHTML = composeBody;
    const text = (tmp.textContent || "").trim();
    if (!text) return;
    const cc = composeCc
      ? composeCc.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    replyMutation.mutate({
      id: selectedId,
      body: composeBody,
      replyAll,
      cc,
      attachments,
    });
  };

  // Insert a merge-field token at the end of the rich-text body. We append rather
  // than splice into the editor's caret position to keep the integration simple.
  const handleInsertVariable = (token: string) => {
    setComposeBody((prev) => {
      const trimmed = (prev || "").trim();
      if (!trimmed || trimmed === "<p></p>") return `<p>${token}</p>`;
      // If body ends with a closing <p>, slot the token in before it.
      if (trimmed.endsWith("</p>")) return `${trimmed.slice(0, -4)} ${token}</p>`;
      return `${trimmed} ${token}`;
    });
  };

  const handleAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const PER_FILE_LIMIT = 10 * 1024 * 1024;
    const TOTAL_LIMIT = 25 * 1024 * 1024;
    const next: AttachmentDraft[] = [];
    let runningTotal = attachments.reduce((s, a) => s + a.size, 0);
    for (const f of files) {
      if (f.size > PER_FILE_LIMIT) {
        toast({
          title: "Attachment too large",
          description: `"${f.name}" exceeds the 10MB per-file limit.`,
          variant: "destructive",
        });
        continue;
      }
      if (runningTotal + f.size > TOTAL_LIMIT) {
        toast({
          title: "Attachments too large",
          description: `Total attachments must stay under 25MB.`,
          variant: "destructive",
        });
        break;
      }
      const buf = await f.arrayBuffer();
      const bytes = new Uint8Array(buf);
      // Build base64 in chunks to avoid stack overflow on large files.
      let bin = "";
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
      }
      const contentBase64 = btoa(bin);
      next.push({
        filename: f.name,
        contentType: f.type || "application/octet-stream",
        contentBase64,
        size: f.size,
      });
      runningTotal += f.size;
    }
    if (next.length > 0) setAttachments((prev) => [...prev, ...next]);
    // Reset the input so re-selecting the same file fires onChange again.
    e.target.value = "";
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  // Compute the "Re:" subject preview that the backend will use, so users can see
  // what the recipient will get before they hit Send.
  const previewReplySubject = (() => {
    const base = detail?.message.subject || "";
    if (!base) return "(no subject)";
    return /^re:\s*/i.test(base) ? base : `Re: ${base}`;
  })();

  const totalUnread = threads.reduce((sum, t) => sum + t.unreadCount, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4 h-full" data-testid="inbox-panel">
      {/* List pane */}
      <Card className="flex flex-col overflow-hidden">
        <div className="flex flex-col gap-2 px-3 py-2 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <InboxIcon className="w-4 h-4" />
              <span className="font-medium text-sm">Inbox</span>
              <Badge variant="outline" className="text-xs" data-testid="badge-inbox-count">
                {viewMode === "priority" ? priorityRows.length : threads.length}
              </Badge>
              {totalUnread > 0 && (
                <Badge className="text-xs" data-testid="badge-inbox-unread">
                  {totalUnread} unread
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="unread-only" className="text-xs cursor-pointer">
                Unread only
              </Label>
              <Switch
                id="unread-only"
                checked={unreadOnly}
                onCheckedChange={setUnreadOnly}
                data-testid="switch-unread-only"
              />
              <Popover open={prefsOpen} onOpenChange={setPrefsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    data-testid="button-channel-prefs"
                    title="Channel preferences"
                  >
                    <Settings2 className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="end">
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold">My channel preferences</h4>
                      <p className="text-xs text-muted-foreground">
                        Per-user toggles for AI Reply Management. Drafts auto-
                        appear in the inbox; Approve still pre-fills only.
                      </p>
                    </div>
                    {!channelPrefs ? (
                      <div className="flex items-center justify-center py-3">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      (["email", "sms"] as const).map((ch) => {
                        const labels = { email: "Email", sms: "SMS" };
                        const pref = channelPrefs[ch];
                        return (
                          <div
                            key={ch}
                            className="space-y-1.5 rounded-md border p-2"
                            data-testid={`channel-pref-${ch}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium">{labels[ch]}</span>
                              <Switch
                                checked={pref.enabled}
                                disabled={updateChannelPref.isPending}
                                onCheckedChange={(enabled) =>
                                  updateChannelPref.mutate({ channel: ch, enabled })
                                }
                                data-testid={`switch-channel-enabled-${ch}`}
                              />
                            </div>
                            <div className="flex items-center justify-between text-[11px] text-muted-foreground pl-1">
                              <span>AI auto-draft</span>
                              <Switch
                                checked={pref.aiDraftEnabled}
                                disabled={!pref.enabled || updateChannelPref.isPending}
                                onCheckedChange={(aiDraftEnabled) =>
                                  updateChannelPref.mutate({ channel: ch, aiDraftEnabled })
                                }
                                data-testid={`switch-channel-draft-${ch}`}
                              />
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => (viewMode === "priority" ? refetchPriority() : refetch())}
                data-testid="button-refresh-inbox"
                title="Refresh"
              >
                <RefreshCw
                  className={cn(
                    "w-4 h-4",
                    (viewMode === "priority" ? priorityLoading : listLoading) && "animate-spin",
                  )}
                />
              </Button>
            </div>
          </div>
          <div className="flex gap-1 -mb-1">
            <button
              type="button"
              onClick={() => setViewMode("all")}
              className={cn(
                "text-xs px-2 py-1 rounded-md transition-colors",
                viewMode === "all" ? "bg-muted font-medium" : "text-muted-foreground hover:bg-muted/50",
              )}
              data-testid="button-tab-all"
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setViewMode("priority")}
              className={cn(
                "text-xs px-2 py-1 rounded-md transition-colors inline-flex items-center gap-1",
                viewMode === "priority" ? "bg-muted font-medium" : "text-muted-foreground hover:bg-muted/50",
              )}
              data-testid="button-tab-priority"
            >
              <Flame className="w-3 h-3" />
              Priority
            </button>
          </div>
          <div className="relative">
            <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subject, sender, or body…"
              className="pl-7 h-8 text-xs"
              data-testid="input-inbox-search"
            />
          </div>
        </div>
        <ScrollArea className="flex-1">
          {viewMode === "priority" ? (
            priorityLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : priorityRows.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground" data-testid="text-priority-empty">
                <Flame className="w-8 h-8 mx-auto mb-2 opacity-40" />
                Nothing in the priority queue
              </div>
            ) : (
              <div className="divide-y">
                {priorityRows.map((r) => {
                  const cat = r.aiCategory;
                  const lastDate = r.receivedAt ? new Date(r.receivedAt) : null;
                  return (
                    <button
                      key={r.id}
                      onClick={() => {
                        setSelectedId(r.id);
                        setSelectedNonEmail(null);
                        setNonEmailComposeOpen(false);
                        setNonEmailComposeBody("");
                        setComposeOpen(false);
                      }}
                      className={cn(
                        "w-full text-left p-3 hover:bg-muted/50 transition-colors block",
                        selectedId === r.id && "bg-muted",
                        !r.isRead && "bg-blue-50/40 dark:bg-blue-950/20",
                      )}
                      data-testid={`button-priority-row-${r.id}`}
                    >
                      <div className="flex items-start gap-2">
                        {r.isRead ? (
                          <MailOpen className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                        ) : (
                          <Mail className="w-4 h-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span
                              className={cn("text-sm truncate", !r.isRead && "font-semibold")}
                              data-testid={`text-priority-from-${r.id}`}
                            >
                              {fromDisplay(r.fromEmail) || "(unknown)"}
                            </span>
                            <span className="text-[10px] text-muted-foreground shrink-0">
                              {lastDate ? lastDate.toLocaleDateString() : ""}
                            </span>
                          </div>
                          <p className={cn("text-sm truncate mt-0.5", !r.isRead && "font-medium")}>
                            {r.subject || "(no subject)"}
                          </p>
                          <div className="flex items-center gap-1.5 mt-1">
                            {cat && (
                              <span
                                className={cn(
                                  "text-[10px] px-1.5 py-0.5 rounded font-medium",
                                  CATEGORY_TONE[cat],
                                )}
                                data-testid={`badge-priority-category-${r.id}`}
                              >
                                {CATEGORY_LABELS[cat]}
                              </span>
                            )}
                            {r.draft && r.draft.status === "pending" && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-100 inline-flex items-center gap-0.5"
                                data-testid={`badge-priority-draft-${r.id}`}
                              >
                                <Sparkles className="w-2.5 h-2.5" />
                                Draft ready
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          ) : listLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : threads.length === 0 ? (
            // Email list empty — still render the non-email section so SMS/IG/FB
            // drafts remain reachable (architect review #2 fix). When BOTH lists
            // are empty, show the standard empty state.
            nonEmailThreads.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground" data-testid="text-inbox-empty">
                <InboxIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                {debouncedSearch ? "No matching conversations" : "No conversations"}
              </div>
            ) : (
              <div className="divide-y" data-testid="section-non-email-threads-only">
                <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">
                  Other channels
                </div>
                {nonEmailThreads.map((nt) => {
                  const lastDate = nt.lastReceivedAt ? new Date(nt.lastReceivedAt) : null;
                  const isSelected = selectedNonEmail?.threadKey === nt.threadKey;
                  return (
                    <button
                      key={nt.threadKey}
                      onClick={() => {
                        setSelectedId(null);
                        setNonEmailComposeOpen(false);
                        setNonEmailComposeBody("");
                        setSelectedNonEmail({
                          channel: nt.channel,
                          sourceId: nt.latestSourceId,
                          threadKey: nt.threadKey,
                          fromHandle: nt.fromHandle,
                          body: nt.body || nt.preview,
                        });
                      }}
                      className={cn(
                        "w-full text-left p-3 hover:bg-muted/50 transition-colors block",
                        isSelected && "bg-muted",
                      )}
                      data-testid={`button-non-email-thread-${nt.threadKey}`}
                    >
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-violet-500" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm truncate font-medium">{nt.fromHandle}</span>
                            <span className="text-[10px] text-muted-foreground shrink-0">
                              {lastDate ? lastDate.toLocaleDateString() : ""}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                              {channelLabel(nt.channel)}
                            </Badge>
                            {nt.latestDraft && nt.latestDraft.status === "pending" && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-100 inline-flex items-center gap-0.5">
                                <Sparkles className="w-2.5 h-2.5" />
                                Draft ready
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{nt.preview}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            <div className="divide-y">
              {threads.map((t) => {
                const unread = t.unreadCount > 0;
                const lastDate = t.lastReceivedAt ? new Date(t.lastReceivedAt) : null;
                return (
                  <button
                    key={t.threadKey}
                    onClick={() => {
                      setSelectedNonEmail(null);
                      handleSelect(t);
                    }}
                    className={cn(
                      "w-full text-left p-3 hover:bg-muted/50 transition-colors block",
                      selectedId === t.latestMessageId && "bg-muted",
                      unread && "bg-blue-50/40 dark:bg-blue-950/20",
                    )}
                    data-testid={`button-inbox-thread-${t.threadKey}`}
                  >
                    <div className="flex items-start gap-2">
                      {unread ? (
                        <Mail className="w-4 h-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
                      ) : (
                        <MailOpen className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={cn("text-sm truncate", unread && "font-semibold")}
                            data-testid={`text-thread-from-${t.threadKey}`}
                          >
                            {t.participants.slice(0, 2).map(fromDisplay).join(", ") || "(unknown)"}
                            {t.participants.length > 2 && ` +${t.participants.length - 2}`}
                          </span>
                          <span className="text-[10px] text-muted-foreground shrink-0 flex items-center gap-1">
                            {t.messageCount > 1 && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1 py-0 h-4"
                                data-testid={`badge-thread-count-${t.threadKey}`}
                              >
                                {t.messageCount}
                              </Badge>
                            )}
                            {lastDate ? lastDate.toLocaleDateString() : ""}
                          </span>
                        </div>
                        <p
                          className={cn("text-sm truncate mt-0.5", unread && "font-medium")}
                          data-testid={`text-thread-subject-${t.threadKey}`}
                        >
                          {t.subject || "(no subject)"}
                        </p>
                        {t.preview && (
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{t.preview}</p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
              {/* Non-email section is also rendered (below) when the email
                 list is empty — see <NonEmailThreadList/> usage. */}
              {nonEmailThreads.length > 0 && (
                <div data-testid="section-non-email-threads">
                  <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">
                    Other channels
                  </div>
                  {nonEmailThreads.map((nt) => {
                    const lastDate = nt.lastReceivedAt ? new Date(nt.lastReceivedAt) : null;
                    const isSelected = selectedNonEmail?.threadKey === nt.threadKey;
                    return (
                      <button
                        key={nt.threadKey}
                        onClick={() => {
                          setSelectedId(null);
                          // Reset composer state so stale draft text from a prior
                          // recipient never bleeds into the new thread.
                          setNonEmailComposeOpen(false);
                          setNonEmailComposeBody("");
                          setSelectedNonEmail({
                            channel: nt.channel,
                            sourceId: nt.latestSourceId,
                            threadKey: nt.threadKey,
                            fromHandle: nt.fromHandle,
                            body: nt.body || nt.preview,
                          });
                        }}
                        className={cn(
                          "w-full text-left p-3 hover:bg-muted/50 transition-colors block border-t",
                          isSelected && "bg-muted",
                        )}
                        data-testid={`button-non-email-thread-${nt.threadKey}`}
                      >
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-violet-500" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm truncate font-medium" data-testid={`text-non-email-from-${nt.threadKey}`}>
                                {nt.fromHandle}
                              </span>
                              <span className="text-[10px] text-muted-foreground shrink-0">
                                {lastDate ? lastDate.toLocaleDateString() : ""}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                                {channelLabel(nt.channel)}
                              </Badge>
                              {nt.latestDraft && nt.latestDraft.status === "pending" && (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-100 inline-flex items-center gap-0.5"
                                  data-testid={`badge-non-email-draft-${nt.threadKey}`}
                                >
                                  <Sparkles className="w-2.5 h-2.5" />
                                  Draft ready
                                </span>
                              )}
                              {nt.latestDraft?.aiCategory && (
                                <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-medium", CATEGORY_TONE[nt.latestDraft.aiCategory])}>
                                  {CATEGORY_LABELS[nt.latestDraft.aiCategory]}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{nt.preview}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </Card>

      {/* Detail pane */}
      <Card className="flex flex-col overflow-hidden">
        {selectedNonEmail ? (
          (() => {
            const ne = selectedNonEmail;
            const thread = nonEmailThreads.find((t) => t.threadKey === ne.threadKey);
            const draft = thread?.latestDraft ?? null;
            return (
              <div className="flex flex-col h-full">
                <div className="px-4 py-3 border-b shrink-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" data-testid="text-non-email-detail-channel">
                      {channelLabel(ne.channel)}
                    </Badge>
                    <span className="text-sm font-medium" data-testid="text-non-email-detail-from">
                      {ne.fromHandle}
                    </span>
                  </div>
                </div>
                <ScrollArea className="flex-1">
                  <div className="p-4 space-y-4">
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground mb-1">
                        Inbound message
                      </div>
                      <div
                        className="text-sm whitespace-pre-wrap rounded-md border p-3 bg-muted/30"
                        data-testid="text-non-email-detail-body"
                      >
                        {ne.body || "(empty)"}
                      </div>
                    </div>
                    {draft ? (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3 text-violet-500" /> AI draft reply
                          <Badge variant="outline" className="ml-1 text-[10px]">
                            {draft.status}
                          </Badge>
                          {draft.aiCategory && (
                            <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-medium", CATEGORY_TONE[draft.aiCategory])}>
                              {CATEGORY_LABELS[draft.aiCategory]}
                            </span>
                          )}
                        </div>
                        <div
                          className="text-sm whitespace-pre-wrap rounded-md border p-3 bg-violet-50/40 dark:bg-violet-950/20"
                          data-testid="text-non-email-draft-body"
                        >
                          {draft.body || draft.bodyPreview}
                        </div>
                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                          <Button
                            size="sm"
                            disabled={
                              draft.status !== "pending" || approveNonEmailDraftMutation.isPending
                            }
                            onClick={() => {
                              setNonEmailComposeBody(draft.body || draft.bodyPreview || "");
                              setNonEmailComposeOpen(true);
                              approveNonEmailDraftMutation.mutate({
                                channel: ne.channel,
                                sourceId: ne.sourceId,
                              });
                            }}
                            data-testid="button-non-email-approve"
                          >
                            Approve & pre-fill
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setNonEmailComposeBody(draft.body || draft.bodyPreview || "");
                              setNonEmailComposeOpen(true);
                            }}
                            data-testid="button-non-email-edit"
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={regenerateNonEmailDraftMutation.isPending}
                            onClick={() =>
                              regenerateNonEmailDraftMutation.mutate({
                                channel: ne.channel,
                                sourceId: ne.sourceId,
                              })
                            }
                            data-testid="button-non-email-redraft"
                          >
                            <RotateCw className={cn("w-3.5 h-3.5 mr-1", regenerateNonEmailDraftMutation.isPending && "animate-spin")} />
                            Re-draft
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={
                              draft.status !== "pending" || dismissNonEmailDraftMutation.isPending
                            }
                            onClick={() =>
                              dismissNonEmailDraftMutation.mutate({
                                channel: ne.channel,
                                sourceId: ne.sourceId,
                              })
                            }
                            data-testid="button-non-email-dismiss"
                          >
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-xs text-muted-foreground" data-testid="text-non-email-no-draft">
                          No AI draft yet for this message.
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={regenerateNonEmailDraftMutation.isPending}
                          onClick={() =>
                            regenerateNonEmailDraftMutation.mutate({
                              channel: ne.channel,
                              sourceId: ne.sourceId,
                            })
                          }
                          data-testid="button-non-email-redraft-empty"
                        >
                          <Sparkles className="w-3.5 h-3.5 mr-1" />
                          Generate AI draft
                        </Button>
                      </div>
                    )}
                    {/* Inline composer — Approve/Edit/Re-draft pre-fill this
                       textarea; Send posts to the per-channel /send route. No
                       auto-send anywhere in this surface. */}
                    {nonEmailComposeOpen && (
                      <div className="border-t pt-4 space-y-2" data-testid="non-email-composer">
                        <div className="text-xs font-semibold text-muted-foreground">
                          Reply via {channelLabel(ne.channel)}
                        </div>
                        <Textarea
                          value={nonEmailComposeBody}
                          onChange={(e) => setNonEmailComposeBody(e.target.value)}
                          placeholder={`Type your ${channelLabel(ne.channel)} reply…`}
                          rows={6}
                          className="font-mono text-sm"
                          data-testid="textarea-non-email-compose"
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            disabled={
                              !nonEmailComposeBody.trim() || sendNonEmailMutation.isPending
                            }
                            onClick={() =>
                              sendNonEmailMutation.mutate({
                                channel: ne.channel,
                                sourceId: ne.sourceId,
                                body: nonEmailComposeBody.trim(),
                              })
                            }
                            data-testid="button-non-email-send"
                          >
                            {sendNonEmailMutation.isPending ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            ) : (
                              <Send className="w-3.5 h-3.5 mr-1" />
                            )}
                            Send
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setNonEmailComposeOpen(false);
                              setNonEmailComposeBody("");
                            }}
                            data-testid="button-non-email-cancel"
                          >
                            Cancel
                          </Button>
                          <span className="text-[11px] text-muted-foreground">
                            Nothing sends until you click Send.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })()
        ) : !selectedId ? (
          <div
            className="flex-1 flex items-center justify-center text-sm text-muted-foreground"
            data-testid="text-detail-empty"
          >
            Select a conversation to view it
          </div>
        ) : detailLoading || !detail ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="px-4 pt-3 pb-2 border-b shrink-0">
              <div className="flex items-center gap-2 flex-wrap mb-2.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setComposeMode("reply");
                    setReplyAll(false);
                    setComposeBody("");
                    setComposeCc("");
                    setComposeOpen(true);
                  }}
                  data-testid="button-reply"
                >
                  <Reply className="w-4 h-4 mr-1" />
                  Reply
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setComposeMode("reply");
                    setReplyAll(true);
                    setComposeBody("");
                    setComposeCc("");
                    setComposeOpen(true);
                  }}
                  data-testid="button-reply-all"
                >
                  <ReplyAll className="w-4 h-4 mr-1" />
                  Reply all
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleStartForward}
                  data-testid="button-forward"
                >
                  <ForwardIcon className="w-4 h-4 mr-1" />
                  Forward
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleMarkUnread}
                  disabled={markReadMutation.isPending}
                  data-testid="button-mark-unread"
                  title="Mark as unread"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Mark unread
                </Button>
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold truncate" data-testid="text-detail-subject">
                  {detail.message.subject || "(no subject)"}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {detail.thread.length > 0 ? (
                    <>
                      {detail.thread.length} message{detail.thread.length === 1 ? "" : "s"}
                    </>
                  ) : (
                    <>
                      From: <span className="font-medium" data-testid="text-detail-from">{fromDisplay(detail.message.fromEmail)}</span>
                    </>
                  )}
                </p>
                {/* Category chip + manual override dropdown. Hidden until the
                    AI categorize pass completes (or never appears for workspaces
                    that have AI Reply Management disabled). */}
                {(detail.message as any).aiCategory && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            "text-[11px] px-2 py-0.5 rounded font-medium hover-elevate",
                            CATEGORY_TONE[(detail.message as any).aiCategory as InboxCategory],
                          )}
                          data-testid="badge-detail-category"
                        >
                          {CATEGORY_LABELS[(detail.message as any).aiCategory as InboxCategory]}
                          {typeof (detail.message as any).aiCategoryConfidence === "number" && (
                            <span className="ml-1 opacity-70">
                              {(detail.message as any).aiCategoryConfidence}%
                            </span>
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {(Object.keys(CATEGORY_LABELS) as InboxCategory[]).map((c) => (
                          <DropdownMenuItem
                            key={c}
                            onClick={() => overrideCategoryMutation.mutate({ id: detail.message.id, category: c })}
                            data-testid={`option-override-${c}`}
                          >
                            {CATEGORY_LABELS[c]}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {(detail.message as any).aiCategoryReason && (
                      <span
                        className="text-[10px] text-muted-foreground italic truncate max-w-[260px]"
                        title={(detail.message as any).aiCategoryReason}
                        data-testid="text-detail-category-reason"
                      >
                        {(detail.message as any).aiCategoryReason}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5"
                      onClick={() => categorizeMutation.mutate(detail.message.id)}
                      disabled={categorizeMutation.isPending}
                      data-testid="button-recategorize"
                      title="Re-run AI categorize"
                    >
                      <RotateCw className={cn("w-3 h-3", categorizeMutation.isPending && "animate-spin")} />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <ScrollArea className="flex-1 min-h-0 px-4 py-3">
              {detail.thread.length > 0 ? (
                <div className="space-y-3">
                  {detail.thread.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      className={cn(
                        "rounded-md border p-3",
                        item.type === "sent"
                          ? "bg-blue-50/40 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900"
                          : "bg-background",
                      )}
                      data-testid={`thread-item-${item.id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-medium" data-testid={`text-thread-sender-${item.id}`}>
                          {item.type === "sent"
                            ? `${item.sentByName || "You"} → ${(item.to || []).map(fromDisplay).join(", ")}`
                            : `${fromDisplay(item.from)}`}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{formatWhen(item.sentAt)}</span>
                      </div>
                      {item.bodyHtml ? (
                        <div
                          className="prose prose-sm dark:prose-invert max-w-none"
                          dangerouslySetInnerHTML={{ __html: sanitize(item.bodyHtml) }}
                        />
                      ) : (
                        <pre className="whitespace-pre-wrap text-sm font-sans">{item.bodyPlainText || ""}</pre>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div data-testid="text-detail-body">
                  {detail.message.bodyHtml ? (
                    <div
                      className="prose prose-sm dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: sanitize(detail.message.bodyHtml) }}
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm font-sans">
                      {detail.message.bodyPlainText || ""}
                    </pre>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* ── AI Reply Management — draft card ─────────────────────────
                Renders when the inbound has a pending AI draft. "Approve" loads
                the draft into the existing reply editor — the actual SEND still
                goes through the user clicking the Send button below, preserving
                the no-auto-send contract. Hidden once the user opens compose so
                the editor can use the full vertical space. */}
            {!composeOpen && detail.draft && detail.draft.status === "pending" && (
              <div
                className="border-t bg-violet-50/40 dark:bg-violet-950/20 p-3 space-y-2 shrink-0"
                data-testid="ai-draft-card"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs font-medium">
                    <Sparkles className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
                    AI suggested reply
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 capitalize">
                      {detail.draft.tone}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Select
                      value={draftTone}
                      onValueChange={(v) => setDraftTone(v as typeof draftTone)}
                    >
                      <SelectTrigger
                        className="h-6 text-[11px] w-28"
                        data-testid="select-draft-tone"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="concise">Concise</SelectItem>
                      </SelectContent>
                    </Select>
                    {/* Task #634 — length selector. Defaults to workspace setting. */}
                    <Select
                      value={draftLength}
                      onValueChange={(v) => setDraftLength(v as typeof draftLength)}
                    >
                      <SelectTrigger
                        className="h-6 text-[11px] w-24"
                        data-testid="select-draft-length"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Short</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="detailed">Detailed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => generateDraftMutation.mutate({ id: detail.message.id, tone: draftTone, length: draftLength })}
                      disabled={generateDraftMutation.isPending}
                      data-testid="button-redraft"
                      title="Re-draft"
                    >
                      <RotateCw className={cn("w-3.5 h-3.5", generateDraftMutation.isPending && "animate-spin")} />
                    </Button>
                  </div>
                </div>
                <div
                  className="rounded-md border bg-background p-2 text-sm prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitize(detail.draft.body) }}
                  data-testid="text-draft-body"
                />
                <div className="flex items-center justify-end gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => dismissDraftMutation.mutate(detail.message.id)}
                    disabled={dismissDraftMutation.isPending}
                    data-testid="button-dismiss-draft"
                  >
                    <XIcon className="w-3.5 h-3.5 mr-1" />
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setComposeMode("reply");
                      setReplyAll(false);
                      setComposeCc("");
                      setComposeBody(detail.draft!.body);
                      setComposeOpen(true);
                    }}
                    data-testid="button-edit-draft"
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      // "Approve" = pre-fill the reply editor with the draft.
                      // The user still has to hit Send below — this is the
                      // one-click approve, not auto-send.
                      setComposeMode("reply");
                      setReplyAll(false);
                      setComposeCc("");
                      setComposeBody(detail.draft!.body);
                      setComposeOpen(true);
                    }}
                    data-testid="button-approve-draft"
                  >
                    <Check className="w-3.5 h-3.5 mr-1" />
                    Approve
                  </Button>
                </div>
              </div>
            )}
            {/* No draft yet, but the message has been categorized as actionable —
                offer a single "Generate AI draft" button. */}
            {!composeOpen
              && (!detail.draft || detail.draft.status === "dismissed")
              && (detail.message as any).aiCategory
              && !CATEGORIES_WITHOUT_DRAFT.has((detail.message as any).aiCategory as InboxCategory)
              && (
                <div className="border-t p-3 shrink-0" data-testid="ai-draft-prompt">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateDraftMutation.mutate({ id: detail.message.id, tone: draftTone, length: draftLength })}
                    disabled={generateDraftMutation.isPending}
                    data-testid="button-generate-draft"
                  >
                    {generateDraftMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5 mr-1" />
                    )}
                    Generate AI draft
                  </Button>
                </div>
              )}

            {composeOpen && (
              <div className="border-t p-3 space-y-2 shrink-0" data-testid={composeMode === "forward" ? "compose-forward" : "compose-reply"}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">
                    {composeMode === "forward"
                      ? <>Forward <span className="font-mono">{detail.message.subject || "(no subject)"}</span></>
                      : <>{replyAll ? "Reply all to" : "Reply to"}{" "}<span className="font-mono">{fromDisplay(detail.message.fromEmail)}</span></>}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={resetCompose}
                    data-testid="button-cancel-compose"
                  >
                    Cancel
                  </Button>
                </div>
                <div className="text-[11px] text-muted-foreground" data-testid="text-compose-subject-preview">
                  Subject:{" "}
                  <span className="font-mono">
                    {composeMode === "forward"
                      ? (() => {
                          const base = detail.message.subject || "";
                          if (!base) return "(no subject)";
                          return /^(?:fwd?|fw):\s*/i.test(base) ? base : `Fwd: ${base}`;
                        })()
                      : previewReplySubject}
                  </span>
                </div>
                {composeMode === "forward" && (
                  <div className="space-y-1">
                    <Label htmlFor="forward-to-input" className="text-xs">
                      To <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="forward-to-input"
                      type="email"
                      value={forwardTo}
                      onChange={(e) => setForwardTo(e.target.value)}
                      placeholder="recipient@example.com"
                      data-testid="input-forward-to"
                    />
                  </div>
                )}
                {composeMode === "reply" && emailTemplates.filter(t => !t.hiddenFromPickers).length > 0 && (
                  <div className="space-y-1">
                    <Label htmlFor="reply-template-select" className="text-xs">
                      Template (optional)
                    </Label>
                    <Select
                      value={selectedTemplateId ?? "none"}
                      onValueChange={handleTemplateChange}
                    >
                      <SelectTrigger
                        id="reply-template-select"
                        data-testid="select-reply-template"
                      >
                        <SelectValue placeholder="Apply a template…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No template</SelectItem>
                        {/* Task #696: hide picker-hidden templates from the
                            inbox reply picker. The lookup that resolves the
                            selected id (handleTemplateChange) still works for
                            any template that was selected before being hidden. */}
                        {emailTemplates.filter(t => !t.hiddenFromPickers).map((template) => (
                          <SelectItem
                            key={template.id}
                            value={template.id}
                            data-testid={`option-reply-template-${template.id}`}
                          >
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <CcEmailInput
                  value={composeCc}
                  onChange={setComposeCc}
                  label="Cc"
                  placeholder="Add CC recipients (comma-separated)…"
                  data-testid="input-compose-cc"
                />
                <RichTextEditor
                  value={composeBody}
                  onChange={setComposeBody}
                  placeholder={composeMode === "forward" ? "Add a message before forwarding…" : "Type your reply…"}
                  minHeight="160px"
                />

                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="list-reply-attachments">
                    {attachments.map((a, i) => (
                      <Badge
                        key={`${a.filename}-${i}`}
                        variant="secondary"
                        className="text-xs gap-1"
                        data-testid={`badge-reply-attachment-${i}`}
                      >
                        <Paperclip className="w-3 h-3" />
                        <span className="max-w-[160px] truncate">{a.filename}</span>
                        <span className="opacity-60">({Math.max(1, Math.round(a.size / 1024))} KB)</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(i)}
                          className="ml-0.5 hover:text-destructive"
                          data-testid={`button-remove-attachment-${i}`}
                          aria-label={`Remove ${a.filename}`}
                        >
                          <XIcon className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <Label
                      htmlFor="reply-attachment-input"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded-md cursor-pointer hover-elevate"
                      data-testid="label-reply-attachment"
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                      Attach
                    </Label>
                    <input
                      id="reply-attachment-input"
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleAttachmentChange}
                      data-testid="input-reply-attachment"
                    />
                    <EmailVariablesSelector onInsert={handleInsertVariable} />
                  </div>
                  {composeMode === "forward" ? (
                    <Button
                      onClick={handleSendForward}
                      disabled={forwardMutation.isPending}
                      data-testid="button-send-forward"
                    >
                      {forwardMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Forwarding…
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          Send forward
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSendReply}
                      disabled={replyMutation.isPending}
                      data-testid="button-send-reply"
                    >
                      {replyMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          Send reply
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
